/**
 * Pure rules for showing the AI's durable questions (R1 Q2, technical design
 * §10.4). Kept out of the components so each rule has one test.
 */

import type {
  PackAsk,
  QuestionAsk,
  QuestionContinuation,
  QuestionDetails,
  QuestionOption,
} from '@/lib/api'
import {
  QUESTION_REASON_NOT_FOUND,
  QUESTION_REASON_UNCONFIRMED,
  QUESTION_REASON_UNSUPPORTED,
  QUESTION_UPDATE_AFTER_CURRENT,
  QUESTION_UPDATE_NOTHING,
  QUESTION_UPDATE_UNAVAILABLE,
  QUESTION_UPDATE_STARTED,
} from '@/lib/question-copy'

/** The document overview's "Questions for your team" list; links land here. */
export const DOCUMENT_QUESTIONS_ANCHOR = 'document-questions'

export function isQuestionAsk(ask: PackAsk): ask is QuestionAsk {
  return ask.kind === 'question' && ask.question !== undefined
}

/** Open and deferred questions are still waiting for an answer. */
export function isWaitingQuestion(ask: QuestionAsk): boolean {
  return ask.question.status === 'open' || ask.question.status === 'deferred'
}

/** Answered, or answered and worked into the draft. */
export function isAnsweredQuestion(ask: QuestionAsk): boolean {
  return ask.question.status === 'answered' || ask.question.status === 'incorporated'
}

export interface QuestionProgress {
  answered: number
  /** Every question still in play: waiting or answered. A replaced question is not counted. */
  total: number
  waiting: number
}

/** "X of N answered": answered and incorporated count as answered; superseded ones are left out. */
export function questionProgress(asks: ReadonlyArray<QuestionAsk>): QuestionProgress {
  const answered = asks.filter(isAnsweredQuestion).length
  const waiting = asks.filter(isWaitingQuestion).length
  return { answered, waiting, total: answered + waiting }
}

/** Oldest first, so a question keeps its number and place when it is answered. */
export function questionsInAskedOrder(asks: ReadonlyArray<QuestionAsk>): QuestionAsk[] {
  return asks
    .filter((ask) => ask.question.status !== 'superseded')
    .map((ask, index) => ({ ask, index }))
    .sort((a, b) => a.ask.createdAt.localeCompare(b.ask.createdAt) || a.index - b.index)
    .map(({ ask }) => ask)
}

/**
 * The fixed reasons the server writes for a question it raises, each said in
 * plain words. Matched exactly after trimming; any other reason (most are
 * written by the model) is shown unchanged.
 */
const PLAIN_REASONS: ReadonlyMap<string, string> = new Map([
  ['The draft needs this, and nothing the job gathered states it.', QUESTION_REASON_NOT_FOUND],
  ['The draft says this, and nothing the job gathered backs it.', QUESTION_REASON_UNSUPPORTED],
  ['The plan needs this fact and nothing the job read states it.', QUESTION_REASON_NOT_FOUND],
  ['The draft says something the job could not confirm, and only the team can settle it.', QUESTION_REASON_UNCONFIRMED],
])

/** The helper line under a question: a fixed server reason in plain words, anything else as written; null when blank. */
export function plainQuestionReason(reason: string | null | undefined): string | null {
  const trimmed = reason?.trim() ?? ''
  if (!trimmed) return null
  return PLAIN_REASONS.get(trimmed) ?? trimmed
}

export interface AnswerOptions {
  /** The recommended option, shown first with its reason. Never on a fact question. */
  recommended: QuestionOption | null
  others: QuestionOption[]
}

/**
 * The options to offer. Only a `judgement` or `decision` question may carry a
 * recommendation (the option with a reason); a `fact` question, or one whose
 * kind is unreadable, never shows one even if the data carries a reason.
 */
export function answerOptions(details: QuestionDetails): AnswerOptions {
  const mayRecommend = details.questionKind === 'judgement' || details.questionKind === 'decision'
  const recommendedIndex = mayRecommend
    ? details.options.findIndex((option) => typeof option.reason === 'string' && option.reason.trim().length > 0)
    : -1
  const recommended = recommendedIndex >= 0 ? details.options[recommendedIndex] ?? null : null
  const others = details.options
    .filter((_, index) => index !== recommendedIndex)
    .map((option) => ({ label: option.label }))
  return { recommended, others }
}

/** Waiting questions first, then the rest; oldest first within each. */
export function sortQuestions(asks: ReadonlyArray<QuestionAsk>): QuestionAsk[] {
  return asks
    .map((ask, index) => ({ ask, index }))
    .sort((a, b) => {
      const waitingA = isWaitingQuestion(a.ask) ? 0 : 1
      const waitingB = isWaitingQuestion(b.ask) ? 0 : 1
      if (waitingA !== waitingB) return waitingA - waitingB
      const byTime = a.ask.createdAt.localeCompare(b.ask.createdAt)
      return byTime !== 0 ? byTime : a.index - b.index
    })
    .map(({ ask }) => ask)
}

export interface ContinuationNotice {
  line: string
  /** One line per section the AI could not update. */
  skipped: string[]
}

/** What "Save and update the draft" did, in plain words. */
export function continuationNotice(continuation: QuestionContinuation): ContinuationNotice {
  switch (continuation.state) {
    case 'started':
      return { line: QUESTION_UPDATE_STARTED, skipped: continuation.skipped.map((s) => s.reason) }
    case 'already_started':
      return { line: QUESTION_UPDATE_STARTED, skipped: [] }
    case 'busy':
      return {
        line: continuation.message.trim() || QUESTION_UPDATE_AFTER_CURRENT,
        skipped: continuation.skipped.map((s) => s.reason),
      }
    case 'no_targets':
      return { line: QUESTION_UPDATE_NOTHING, skipped: continuation.skipped.map((s) => s.reason) }
    case 'unavailable':
      return { line: continuation.message.trim() || QUESTION_UPDATE_UNAVAILABLE, skipped: [] }
  }
}
